import { useState } from 'react'
import { X, AlertTriangle, Loader2 } from 'lucide-react'
import { createTenant } from '../../../services/api'

interface Props {
  onClose: () => void
  onCreated: () => void
}

function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 30)
}

export default function AdminCompanyForm({ onClose, onCreated }: Props) {
  const [form, setForm] = useState({
    name: '',
    tenant_id: '',
    admin_email: '',
    plan: 'pro',
    domain: '',
  })
  const [autoSlug, setAutoSlug] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleNameChange(name: string) {
    setForm((f) => ({
      ...f,
      name,
      tenant_id: autoSlug ? nameToSlug(name) : f.tenant_id,
    }))
  }

  function handleIdChange(id: string) {
    setAutoSlug(false)
    setForm((f) => ({ ...f, tenant_id: id.toLowerCase().replace(/[^a-z0-9-]/g, '') }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (form.tenant_id.length < 3) {
      setError('Company ID must be at least 3 characters')
      return
    }
    setCreating(true)
    setError(null)
    try {
      await createTenant({
        tenant_id: form.tenant_id,
        name: form.name,
        admin_email: form.admin_email,
        plan: form.plan,
        domain: form.domain || undefined,
      })
      onCreated()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to create company')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-xl animate-fade-in">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">New Company</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Acme Corp"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 outline-none"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company ID</label>
            <input
              type="text"
              required
              value={form.tenant_id}
              onChange={(e) => handleIdChange(e.target.value)}
              placeholder="acme-corp"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 outline-none font-mono"
            />
            <p className="mt-1 text-xs text-gray-400">Lowercase letters, numbers, and dashes only</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
            <input
              type="email"
              required
              value={form.admin_email}
              onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))}
              placeholder="admin@company.com"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select
                value={form.plan}
                onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white"
              >
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <input
                type="text"
                value={form.domain}
                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                placeholder="company.com"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
            >
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Company
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
